/**
 * Video API Client - Image-to-Video Generation
 */

import axios from 'axios';
import { getAuthToken } from './auth';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

// Types
export interface VideoPose {
  id: string;
  name: string;
  preview_image?: string;
  prompt?: string; // Only visible to admin
}

export interface VideoConfig {
  poses: VideoPose[];
  size_options: string[];
  duration_options: number[];
  default_params: {
    num_inference_steps: number;
    guidance: number;
    size: string;
    duration: number;
    flow_shift: number;
    enable_prompt_optimization: boolean;
    enable_safety_checker: boolean;
    negative_prompt: string;
  };
}

export interface VideoGenerationRequest {
  image_url?: string;
  image_file?: File;  // For file upload
  pose_id?: string;
  user_prompt?: string;
  advanced?: {
    duration?: number;
    seed?: number;
    size?: string;
    num_inference_steps?: number;
    guidance?: number;
    flow_shift?: number;
    enable_prompt_optimization?: boolean;
    enable_safety_checker?: boolean;
    negative_prompt?: string;
  };
}

export interface VideoGenerationResponse {
  success: boolean;
  job_id?: string;
  status?: string;
  message?: string;
}

export interface VideoStatus {
  success: boolean;
  job_id: string;
  status: 'IN_QUEUE' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
  output_url?: string;
  error?: string;
  execution_time?: number;
}

/**
 * Pobierz listę dostępnych póz i konfigurację
 */
export async function getVideoPoses(): Promise<VideoConfig> {
  const token = getAuthToken();

  const response = await axios.get(`${API_BASE_URL}/api/video/poses`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.data.success) {
    throw new Error(response.data.message || 'Failed to get poses');
  }

  return {
    poses: response.data.poses || [],
    size_options: response.data.size_options || ['1280*720', '720*1280'],
    duration_options: response.data.duration_options || [5, 10],
    default_params: response.data.default_params || {},
  };
}

/**
 * Rozpocznij generację video
 */
export async function generateVideo(
  request: VideoGenerationRequest
): Promise<VideoGenerationResponse> {
  const token = getAuthToken();

  // Use FormData if image_file provided, otherwise JSON
  if (request.image_file) {
    const formData = new FormData();
    formData.append('image_file', request.image_file);

    if (request.pose_id) {
      formData.append('pose_id', request.pose_id);
    }

    if (request.user_prompt) {
      formData.append('user_prompt', request.user_prompt);
    }

    if (request.advanced) {
      formData.append('advanced', JSON.stringify(request.advanced));
    }

    const response = await axios.post(
      `${API_BASE_URL}/api/video/generate`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return response.data;
  } else {
    // JSON mode with image_url
    const response = await axios.post(
      `${API_BASE_URL}/api/video/generate`,
      request,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    return response.data;
  }
}

/**
 * Pobierz status generacji video
 */
export async function getVideoStatus(jobId: string): Promise<VideoStatus> {
  const token = getAuthToken();

  const response = await axios.get(
    `${API_BASE_URL}/api/video/status/${jobId}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  return response.data;
}

/**
 * Poll status aż do zakończenia (helper function)
 */
export async function waitForVideoCompletion(
  jobId: string,
  onProgress?: (status: VideoStatus) => void,
  pollInterval: number = 5000,
  maxWaitTime: number = 600000
): Promise<VideoStatus> {
  const startTime = Date.now();

  while (true) {
    const status = await getVideoStatus(jobId);

    if (onProgress) {
      onProgress(status);
    }

    if (['COMPLETED', 'FAILED', 'CANCELLED'].includes(status.status)) {
      return status;
    }

    const elapsed = Date.now() - startTime;
    if (elapsed > maxWaitTime) {
      throw new Error(`Timeout waiting for video completion (${maxWaitTime}ms)`);
    }

    await new Promise((resolve) => setTimeout(resolve, pollInterval));
  }
}
